import React, { useEffect, useState } from 'react'
import { ArrowCounterClockwise } from '@phosphor-icons/react'
import {
  useLeagueDirectCheckIn,
  useLeagueDirectCheckOut,
  useLeagueUndoCheckInOut,
} from 'api/league/LeagueAttendance/leagueAttendance'
import { AttendeeRecord, leagueAttendanceStore } from 'state/league/leagueAttendance'
import { StatusList } from '../../../Tabs/Attendances/Modals/CheckInOut/constants'
import ButtonSpinner from 'components/global/ButtonSpinner'
import { failureMessage } from 'api/global'

interface StatusLabelProps {
  participant: AttendeeRecord
  leagueId: string
  date: string
  recentlyCheckedInOut: string[]
  setRecentlyCheckedInOut: React.Dispatch<React.SetStateAction<string[]>>
}

export const LeagueStatusLabelNone: React.FC<StatusLabelProps> = ({
  participant,
  leagueId,
  date,
  recentlyCheckedInOut,
  setRecentlyCheckedInOut,
}) => {
  const { mutate: directCheckIn, isLoading } = useLeagueDirectCheckIn()

  const handleClick = () => {
    if (new Date(date) > new Date()) {
      failureMessage('Cannot perform this action on future dates')
      return
    }
    const payload = {
      leagueId,
      date,
      familyId: participant.familyId,
      [`${participant.participantType ?? 'kid'}Id`]: participant._id,
    }
    directCheckIn(payload as any, {
      onSuccess: () =>
        setRecentlyCheckedInOut([...recentlyCheckedInOut, participant._id]),
    })
  }

  return (
    <div
      onClick={handleClick}
      style={{ backgroundColor: StatusList[0].color }}
      className="flex flex-center justify-center px-15px py-10px radius-12 ml-15px pointer"
    >
      {isLoading ? (
        <ButtonSpinner size={20} color="#000" />
      ) : (
        <>
          <img src={StatusList[0].img} alt={StatusList[0].value} />
          <h4 className="ml-10px text-12 w-600">{StatusList[0].title}</h4>
        </>
      )}
    </div>
  )
}

export const LeagueStatusLabelCheckin: React.FC<StatusLabelProps> = ({
  participant,
  recentlyCheckedInOut,
  setRecentlyCheckedInOut,
}) => {
  const [showUndo, setShowUndo] = useState(false)
  const { mutate: directCheckOut, isLoading: checkOutLoading } = useLeagueDirectCheckOut()
  const { mutate: undoCheck, isLoading: undoLoading } = useLeagueUndoCheckInOut()

  const handleClick = () => {
    if (!participant.checkInId) return
    directCheckOut(
      { checkInId: participant.checkInId },
      {
        onSuccess: () =>
          setRecentlyCheckedInOut([...recentlyCheckedInOut, participant._id]),
      },
    )
  }

  const handleUndo = () => {
    setShowUndo(false)
    setRecentlyCheckedInOut(recentlyCheckedInOut.filter(id => id !== participant._id))
    if (participant.checkInId) undoCheck(participant.checkInId)
  }

  useEffect(() => {
    setShowUndo(recentlyCheckedInOut.includes(participant._id))
  }, [recentlyCheckedInOut, participant._id])

  useEffect(() => {
    const timer = setTimeout(() => {
      setRecentlyCheckedInOut(prev => prev.filter(id => id !== participant._id))
      setShowUndo(false)
    }, 3000)
    return () => clearTimeout(timer)
  }, [recentlyCheckedInOut, participant._id])

  return (
    <div className="flex gap-10px">
      <div
        onClick={handleClick}
        style={{ backgroundColor: StatusList[3].color }}
        className="flex flex-center justify-center px-15px py-10px radius-12 ml-15px pointer"
      >
        {checkOutLoading || undoLoading ? (
          <ButtonSpinner size={20} color="#000" />
        ) : (
          <>
            <img src={StatusList[3].img} alt={StatusList[3].value} />
            <h4 className="ml-10px text-12 w-600">{StatusList[3].title}</h4>
          </>
        )}
      </div>
      {showUndo && (
        <div
          className="flex flex-col justify-center align-center pointer"
          onClick={handleUndo}
        >
          <ArrowCounterClockwise size={20} color="#000" />
          <p>undo</p>
        </div>
      )}
    </div>
  )
}

export const LeagueStatusLabelCheckout: React.FC<StatusLabelProps> = ({
  participant,
  leagueId,
  date,
  recentlyCheckedInOut,
  setRecentlyCheckedInOut,
}) => {
  const [showUndo, setShowUndo] = useState(false)
  const { mutate: directCheckIn, isLoading: checkInLoading } = useLeagueDirectCheckIn()
  const { mutate: undoCheck, isLoading: undoLoading } = useLeagueUndoCheckInOut()

  const handleClick = () => {
    if (new Date(date) > new Date()) {
      failureMessage('Cannot perform this action on future dates')
      return
    }
    const payload = {
      leagueId,
      date,
      familyId: participant.familyId,
      [`${participant.participantType ?? 'kid'}Id`]: participant._id,
    }
    directCheckIn(payload as any, {
      onSuccess: () =>
        setRecentlyCheckedInOut([...recentlyCheckedInOut, participant._id]),
    })
  }

  const handleUndo = () => {
    setShowUndo(false)
    setRecentlyCheckedInOut(recentlyCheckedInOut.filter(id => id !== participant._id))
    if (participant.checkInId) undoCheck(participant.checkInId)
  }

  useEffect(() => {
    setShowUndo(recentlyCheckedInOut.includes(participant._id))
  }, [recentlyCheckedInOut, participant._id])

  useEffect(() => {
    const timer = setTimeout(() => {
      setRecentlyCheckedInOut(prev => prev.filter(id => id !== participant._id))
      setShowUndo(false)
    }, 3000)
    return () => clearTimeout(timer)
  }, [recentlyCheckedInOut, participant._id])

  return (
    <div className="flex gap-10px">
      <div
        onClick={handleClick}
        style={{ backgroundColor: StatusList[4].color }}
        className="flex flex-center justify-center px-15px py-10px radius-12 ml-15px pointer"
      >
        {checkInLoading || undoLoading ? (
          <ButtonSpinner size={20} color="#000" />
        ) : (
          <>
            <img src={StatusList[4].img} alt={StatusList[4].value} />
            <h4 className="ml-10px text-12 w-600">{StatusList[4].title}</h4>
          </>
        )}
      </div>
      {showUndo && (
        <div
          className="flex flex-col justify-center align-center pointer"
          onClick={handleUndo}
        >
          <ArrowCounterClockwise size={20} color="#000" />
          <p>undo</p>
        </div>
      )}
    </div>
  )
}

export const LeagueStatusLabelCheckinPending: React.FC<StatusLabelProps> = ({
  participant,
  recentlyCheckedInOut,
  setRecentlyCheckedInOut,
}) => {
  const [showUndo, setShowUndo] = useState(false)
  const { mutate: approveCheckIn, isLoading: approveLoading } = useLeagueDirectCheckOut()
  const { mutate: undoCheck, isLoading: undoLoading } = useLeagueUndoCheckInOut()

  const handleClick = () => {
    if (!participant.checkInId) return
    approveCheckIn(
      { checkInId: participant.checkInId },
      {
        onSuccess: () =>
          setRecentlyCheckedInOut([...recentlyCheckedInOut, participant._id]),
      },
    )
  }

  const handleUndo = () => {
    setShowUndo(false)
    setRecentlyCheckedInOut(recentlyCheckedInOut.filter(id => id !== participant._id))
    if (participant.checkInId) undoCheck(participant.checkInId)
  }

  useEffect(() => {
    setShowUndo(recentlyCheckedInOut.includes(participant._id))
  }, [recentlyCheckedInOut, participant._id])

  useEffect(() => {
    const timer = setTimeout(() => {
      setRecentlyCheckedInOut(prev => prev.filter(id => id !== participant._id))
      setShowUndo(false)
    }, 3000)
    return () => clearTimeout(timer)
  }, [recentlyCheckedInOut, participant._id])

  return (
    <div className="flex gap-10px">
      <div
        onClick={handleClick}
        style={{ backgroundColor: StatusList[7].color }}
        className="flex flex-center justify-center px-15px py-10px radius-12 ml-15px pointer"
      >
        {approveLoading ? (
          <ButtonSpinner size={20} color="#000" />
        ) : (
          <>
            <img src={StatusList[7].img} alt={StatusList[7].value} />
            <h4 className="ml-10px text-12 w-600">{StatusList[7].title}</h4>
          </>
        )}
      </div>
      {showUndo && (
        <div
          className="flex flex-col justify-center align-center pointer"
          onClick={handleUndo}
        >
          <ArrowCounterClockwise size={20} color="#000" />
          <p>undo</p>
        </div>
      )}
    </div>
  )
}

export const LeagueStatusLabelCheckoutPending: React.FC<StatusLabelProps> = ({
  participant,
  recentlyCheckedInOut,
  setRecentlyCheckedInOut,
}) => {
  const [showUndo, setShowUndo] = useState(false)
  const { mutate: directCheckOut, isLoading: checkOutLoading } = useLeagueDirectCheckOut()
  const { mutate: undoCheck, isLoading: undoLoading } = useLeagueUndoCheckInOut()

  const handleClick = () => {
    if (!participant.checkInId) return
    directCheckOut(
      { checkInId: participant.checkInId },
      {
        onSuccess: () =>
          setRecentlyCheckedInOut([...recentlyCheckedInOut, participant._id]),
      },
    )
  }

  const handleUndo = () => {
    setShowUndo(false)
    setRecentlyCheckedInOut(recentlyCheckedInOut.filter(id => id !== participant._id))
    if (participant.checkInId) undoCheck(participant.checkInId)
  }

  useEffect(() => {
    setShowUndo(recentlyCheckedInOut.includes(participant._id))
  }, [recentlyCheckedInOut, participant._id])

  useEffect(() => {
    const timer = setTimeout(() => {
      setRecentlyCheckedInOut(prev => prev.filter(id => id !== participant._id))
      setShowUndo(false)
    }, 3000)
    return () => clearTimeout(timer)
  }, [recentlyCheckedInOut, participant._id])

  return (
    <div className="flex gap-10px">
      <div
        onClick={handleClick}
        style={{ backgroundColor: StatusList[5].color }}
        className="flex flex-center justify-center px-15px py-10px radius-12 ml-15px pointer"
      >
        {checkOutLoading || undoLoading ? (
          <ButtonSpinner size={20} color="#000" />
        ) : (
          <>
            <img src={StatusList[5].img} alt={StatusList[5].value} />
            <h4 className="ml-10px text-12 w-600">{StatusList[5].title}</h4>
          </>
        )}
      </div>
      {showUndo && (
        <div
          className="flex flex-col justify-center align-center pointer"
          onClick={handleUndo}
        >
          <ArrowCounterClockwise size={20} color="#000" />
          <p>undo</p>
        </div>
      )}
    </div>
  )
}

export const LeagueStatusLabelPending: React.FC = () => (
  <div
    style={{ backgroundColor: StatusList[6].color }}
    className="flex flex-center justify-center px-15px py-10px radius-12 ml-15px"
  >
    <img src={StatusList[6].img} alt={StatusList[6].value} />
    <h4 className="ml-10px text-12 w-600">{StatusList[6].title}</h4>
  </div>
)

// Drop-in badge: display only — actions are handled by the action button
export const LeagueStatusLabelDropIn: React.FC = () => (
  <div
    style={{ backgroundColor: StatusList[1].color }}
    className="flex flex-center justify-center px-15px py-10px radius-12 ml-15px"
  >
    <img src={StatusList[1].img} alt={StatusList[1].value} />
    <h4 className="ml-10px text-12 w-600">{StatusList[1].title}</h4>
  </div>
)

// Dropped-out badge: static label only
export const LeagueStatusLabelDroppedOut: React.FC = () => (
  <div
    style={{ backgroundColor: StatusList[2].color }}
    className="flex flex-center justify-center px-15px py-10px radius-12 ml-15px"
  >
    <img src={StatusList[2].img} alt={StatusList[2].value} />
    <h4 className="ml-10px text-12 w-600">{StatusList[2].title}</h4>
  </div>
)
